import { useEffect, useState } from "react"
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router"
import { Toaster } from "sonner"
import { client } from "@client/client"
import { getErrorMessage } from "@client/lib/errors"
import { cacheSession, clearCachedSession, getCachedSession } from "@client/lib/session"
import { AdminSessionContext, type AdminSession } from "./session-context"
import { Sidebar } from "./components/sidebar"
import { Header } from "./components/header"
import { DetailPageSkeleton } from "./components/skeleton"
import { CloseIcon } from "./components/icons"
import { NAV_ITEMS, TEAM_NAV_ITEMS, getAccessibleNavItems } from "./navigation"

export function AdminLayoutNew() {
  const { pathname, search } = useLocation()
  const contentActive =
    pathname === "/admin/content" ||
    pathname.startsWith("/admin/newsletters") ||
    pathname.startsWith("/admin/subscribers")
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [session, setSession] = useState<AdminSession | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false)
  const [mobileNavOpen, setMobileNavOpen] = useState(false)
  const navItems = getAccessibleNavItems(NAV_ITEMS, session?.role)
  const teamNavItems = getAccessibleNavItems(TEAM_NAV_ITEMS, session?.role)

  useEffect(() => {
    if (!mobileNavOpen) return
    const previous = document.body.style.overflow
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setMobileNavOpen(false)
    }
    document.body.style.overflow = "hidden"
    document.addEventListener("keydown", onKeyDown)
    return () => {
      document.body.style.overflow = previous
      document.removeEventListener("keydown", onKeyDown)
    }
  }, [mobileNavOpen])

  useEffect(() => {
    let cancelled = false
    const check = async () => {
      try {
        const res = await client.api.admin.auth.me.$get()
        if (res.status === 401) {
          clearCachedSession()
          if (!cancelled) navigate("/admin/login", { replace: true })
          return
        }
        const body = await res.json()
        if (!res.ok && !cancelled) {
          setError(getErrorMessage(body))
          return
        }
        if (!cancelled) {
          const data = "data" in body ? body.data : null
          if (data?.email) cacheSession(data.email)
          if (data) {
            setSession({
              id: data.id,
              email: data.email,
              name: data.name,
              role: data.role,
              avatarUrl: data.avatarUrl,
            })
          }
        }
      } catch {
        const cached = getCachedSession()
        if (cached) {
          if (!cancelled) {
            // For offline mode, set minimal session with viewer role
            setSession({
              id: "",
              email: cached.email,
              name: cached.email.split("@")[0] ?? "Admin",
              role: {
                id: "",
                name: "Viewer",
                permissions: [],
                isSuper: false,
              },
              avatarUrl: null,
            })
          }
        } else if (!cancelled) {
          navigate("/admin/login", { replace: true })
        }
      } finally {
        if (!cancelled) setAuthLoading(false)
      }
    }
    void check()
    return () => {
      cancelled = true
    }
  }, [navigate])

  const logout = async () => {
    try {
      await client.api.admin.auth.logout.$post()
    } finally {
      clearCachedSession()
      navigate("/admin/login", { replace: true })
    }
  }

  return (
    <AdminSessionContext.Provider value={session}>
      <div className="min-h-[100dvh] bg-cream-50 text-ink">
        <Sidebar
          collapsed={sidebarCollapsed}
          onToggle={() => setSidebarCollapsed(!sidebarCollapsed)}
          role={session?.role}
        />

          <div
            aria-hidden={!mobileNavOpen}
            inert={!mobileNavOpen}
            className={`fixed inset-0 z-50 lg:hidden ${mobileNavOpen ? "" : "pointer-events-none"}`}
          >
            <button
              type="button"
              aria-label="Close navigation"
              onClick={() => setMobileNavOpen(false)}
              className={`absolute inset-0 bg-ink/30 backdrop-blur-sm transition-opacity duration-300 motion-reduce:transition-none ${mobileNavOpen ? "opacity-100" : "opacity-0"}`}
            />
            <nav
              className={`relative flex h-full w-[min(19rem,calc(100%-3rem))] flex-col border-r border-gold-500 bg-cream-50 shadow-2xl transition-transform duration-300 ease-out motion-reduce:transition-none ${mobileNavOpen ? "translate-x-0" : "-translate-x-full"}`}
              aria-label="Mobile admin"
            >
              <div className="flex h-[72px] items-center justify-between border-b border-gold-500/50 px-5">
                <Link to="/admin" className="flex items-center gap-3" onClick={() => setMobileNavOpen(false)}>
                  <span className="grid h-10 w-10 flex-none place-items-center overflow-hidden rounded-full bg-forest-950">
                    <img src="/images/home/logo.png" alt="" className="h-full w-full object-contain p-1.5" />
                  </span>
                  <span className="font-display text-lg font-bold text-ink">
                    Redempta Admin
                  </span>
                </Link>
                <button
                  type="button"
                  aria-label="Close navigation"
                  onClick={() => setMobileNavOpen(false)}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-ink/60 transition-colors hover:bg-cream-200 hover:text-ink"
                >
                  <CloseIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="flex flex-1 flex-col gap-1 overflow-y-auto px-3 py-4">
                {navItems.map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMobileNavOpen(false)}
                    className={({ isActive }) => {
                      const active = isActive || (item.to === "/admin/content" && contentActive)
                      return `flex items-center gap-3 rounded-full px-3 py-3 text-sm font-medium transition-colors ${
                        active
                          ? "bg-forest-950 text-white"
                          : "text-ink/70 hover:bg-cream-200 hover:text-ink"
                      }`
                    }}
                  >
                    <item.icon className="h-5 w-5 flex-none" />
                    <span>{item.label}</span>
                  </NavLink>
                ))}

                {teamNavItems.length > 0 && (
                  <>
                    <div className="my-3 border-t border-gold-500/50" />
                    {teamNavItems.map((item) => (
                      <NavLink
                        key={item.to}
                        to={item.to}
                        end={item.end}
                        onClick={() => setMobileNavOpen(false)}
                        className={({ isActive }) =>
                          `flex items-center gap-3 rounded-full px-3 py-3 text-sm font-medium transition-colors ${
                            isActive
                              ? "bg-forest-950 text-white"
                              : "text-ink/70 hover:bg-cream-200 hover:text-ink"
                          }`
                        }
                      >
                        <item.icon className="h-5 w-5 flex-none" />
                        <span>{item.label}</span>
                      </NavLink>
                    ))}
                  </>
                )}
              </div>

              <div className="space-y-3 border-t border-gold-500/50 p-4">
                <div className="flex items-center gap-2 text-xs text-forest-700">
                  <span className="h-2 w-2 rounded-full bg-forest-700" />
                  <span>Admin workspace</span>
                </div>
                <button
                  onClick={() => {
                    setMobileNavOpen(false)
                    void logout()
                  }}
                  className="w-full rounded-full border border-ink/20 px-3 py-3 text-left text-sm font-medium text-ink transition-colors hover:bg-cream-100"
                >
                  Sign out
                </button>
              </div>
            </nav>
          </div>

        <div
          className={`transition-[margin] duration-300 ${
            sidebarCollapsed ? "lg:ml-20" : "lg:ml-[305px]"
          }`}
        >
          <Header
            name={session?.name}
            email={session?.email}
            avatarUrl={session?.avatarUrl}
            onLogout={logout}
            onMobileMenuToggle={() => setMobileNavOpen(true)}
            onProfileUpdated={(name) => setSession((current) => current ? { ...current, name } : current)}
          />
          <main key={`${pathname}${search}`} className="animate-fade-in-up p-5 sm:p-7 lg:p-[50px]">
            {authLoading ? <DetailPageSkeleton /> : error ? <p className="text-sm text-red-600">{error}</p> : <Outlet />}
          </main>
        </div>
      </div>
      <Toaster
        theme="light"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "white",
            border: "1px solid var(--color-gold-500)",
            color: "var(--color-ink)",
            fontSize: "0.875rem",
          },
        }}
      />
    </AdminSessionContext.Provider>
  )
}
